import { Signature } from '@/components/common/Signature';
import { usePharmacistShipment } from '@/context/shipments/PharmacistShipment';
import { Button, Stack, TextField, Typography } from '@montugroup/design-system';
import CalendarToday from '@mui/icons-material/CalendarToday';
import { Paper } from '@mui/material';
import { useRef, useState } from 'react';
import type SignatureCanvas from 'react-signature-canvas';

function ConsignmentAcknowledgementForm() {
  const {
    recipientName,
    setRecipientName,
    date,
    signature,
    setSignature,
    submitAcknowledgement,
    submitted,
    disableForm,
  } = usePharmacistShipment();
  const sigPadRef = useRef<SignatureCanvas | null>(null);
  const [validateForm, setValidateForm] = useState(false);

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const saveSignature = (): string | null => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const dataUrl = sigPadRef.current.toDataURL('image/png');
      setSignature(dataUrl);
      return dataUrl;
    }
    return null;
  };

  const handleSubmit = () => {
    setValidateForm(true);
    const signatureData = saveSignature();
    submitAcknowledgement(signatureData);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 2,
        width: '100%',
        mx: 'auto',
        mb: 4,
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h6">Acknowledgement</Typography>
        <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Update the received quantity to reflect your stock count if it differs from the ordered quantity displayed in
          the table before acknowledging the consignment.
        </Typography>

        {/* Recipient name */}
        <TextField
          fullWidth
          label="Recipient Full Name"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          error={!recipientName.length && validateForm}
          InputProps={{ readOnly: disableForm }}
          helperText={!recipientName.length && validateForm ? 'Recipient name is required' : ''}
        />

        <Signature
          label="Signature"
          value={signature}
          onClear={submitted ? undefined : clearSignature}
          error={!signature && sigPadRef?.current?.isEmpty() && validateForm}
          canvasRef={sigPadRef}
        />

        {/* Acknowledgement date */}
        <TextField
          fullWidth
          label="Acknowledgement Date"
          value={new Date(date).toLocaleDateString()}
          InputProps={{
            readOnly: true,
            startAdornment: <CalendarToday fontSize="small" sx={{ mr: 1 }} />,
          }}
        />

        {/* Submit button */}
        <Button
          variant="contained"
          color={'secondary'}
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={disableForm}
        >
          {disableForm ? 'Acknowledged' : 'Submit'}
        </Button>

        {/* Footer note */}
        {disableForm ? null : (
          <Typography variant="caption" sx={(theme) => ({ color: theme.palette.text.secondary })} align="center">
            This is to confirm my order to supply the detailed controlled drugs. I also acknowledge receipt of the above
            controlled drugs.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export default ConsignmentAcknowledgementForm;
